import React, { useState, useEffect, useCallback, useRef } from "react";
import { api } from "../../lib/api";
import { st } from "../../styles.js";
import { Icon } from "../../icons";
import { BuzerIcon } from "../../BuzerIcons";
import { Modal, Glyph, DueLabel, FPill } from "../../components/ui";
import { Sheet, Button, IconButton, Segmented, SectionHeader, Caption } from "../../components/ui/ds";
import { todayKey, offLabel } from "../../lib/format";
import { fmtDuePretty } from "../../lib/date-sk";
import { resolveIcon, iconChipStyle } from "../../lib/icons-resolve";
import { PlanItemRow } from "./PlanItemRow";
import { REPEAT_LABEL } from "../../lib/constants";
import { AddCard } from "../../components/forms/AddCard";
import { CalendarTab } from "./CalendarTab";

// One info row in the reminder/event detail sheet (icon chip + label + value).
function InfoRow({ icon, label, value }) {
  // v25 — malá holá ikona rovno pri nadpise (bez veľkého chipu s pozadím)
  return (
    <div>
      <Caption icon={icon}>{label}</Caption>
      <span style={{ display: "block", fontSize: 14, color: "var(--neutral-text-main)", marginTop: 3, lineHeight: 1.4 }}>{value}</span>
    </div>
  );
}

export function PlanTab({ s, reload, createWhat, createKey, planFocus, planFocusKey }) {
  // #3 — remember the Zoznam/Kalendár choice per user across reloads. Persist only on the manual
  // toggle (chooseView), so the create flows that jump to "list" don't overwrite the saved choice.
  const _viewKey = "buzer_planview_" + ((s.user && s.user.id) || 0);
  const [view, setView] = useState(() => { try { return localStorage.getItem(_viewKey) === "cal" ? "cal" : "list"; } catch { return "list"; } });
  const chooseView = (v) => { setView(v); try { localStorage.setItem(_viewKey, v); } catch { /* */ } };
  useEffect(() => { window.scrollTo(0, 0); }, [view]);  // A4
  const [addOpen, setAddOpen] = useState(false);
  const [addPrefill, setAddPrefill] = useState(null);  // v16 §9 — {due, type} from a calendar day-tap
  // v-e — when the add form was opened FROM the calendar (onAddForDay), remember it so closing
  // the form returns to the calendar instead of dropping to the list. FAB adds leave this false.
  const [addFromCal, setAddFromCal] = useState(false);
  const [detail, setDetail] = useState(null);          // item shown in the view/edit modal
  const [detailEdit, setDetailEdit] = useState(false); // modal switched to edit mode
  const [pendingDel, setPendingDel] = useState(null);  // v21.10 — položka na potvrdenie zmazania
  // v23 — filter sa pamätá per používateľ aj po refreshi
  const _filterKey = "buzer_planfilter_" + ((s.user && s.user.id) || 0);
  const _savedFilter = (() => { try { return JSON.parse(localStorage.getItem(_filterKey) || "{}"); } catch { return {}; } })();
  const [planFilter, setPlanFilter] = useState(_savedFilter.planFilter || "all"); // "all" | "mine" | "shared"
  const [filterOpen, setFilterOpen] = useState(false);  // v23 — lievik-filter dropdown
  const [typeFilter, setTypeFilter] = useState(_savedFilter.typeFilter || "all");  // v23 — all | reminder | event
  const [catFilter, setCatFilter] = useState(_savedFilter.catFilter != null ? _savedFilter.catFilter : "__all");  // __all | "" | <názov>
  useEffect(() => { try { localStorage.setItem(_filterKey, JSON.stringify({ planFilter, typeFilter, catFilter })); } catch { /* */ } }, [planFilter, typeFilter, catFilter, _filterKey]);
  const [flashId, setFlashId] = useState(null);         // v2 — highlight a newly created item (#72)
  const [doneOpen, setDoneOpen] = useState(false);      // v2 — collapsible Splnené section
  const addFormRef = useRef(null);
  useEffect(() => {
    if (!createKey) return;
    if (createWhat === "plan") { setAddFromCal(false); setView("list"); setDetail(null); setAddPrefill(null); setAddOpen(true); }
    else if (createWhat === "event") { setAddFromCal(false); setView("list"); setDetail(null); setAddPrefill({ type: "event" }); setAddOpen(true); }  // FAB → udalosť
  }, [createKey]);
  // P — deep-link zo zdieľanej poznámky (plan::<id>): vynúť Zoznam view + zruš filtre (nech ju rozsah
  // „Zdieľané"/kategória/termín neschová) a potom ju cez existujúcu flash cestu zvýrazni/odscrolluj.
  useEffect(() => {
    if (!planFocusKey || planFocus == null) return;
    chooseView("list");
    setPlanFilter("all"); setTypeFilter("all"); setCatFilter("__all");
    setFlashId(Number(planFocus));
  }, [planFocusKey]);  // eslint-disable-line react-hooks/exhaustive-deps
  // v2 — scroll to + flash a just-created item (also for FAB creates)
  useEffect(() => {
    if (flashId == null) return;
    const el = document.querySelector(`[data-planid="${flashId}"]`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    const t = setTimeout(() => setFlashId(null), 1900);
    return () => clearTimeout(t);
  }, [flashId]);
  // shared card styles (mockup: grouped cards per section/category)
  const groupCard = { background: "var(--neutral-surface)", border: "1px solid var(--neutral-border)", borderRadius: "var(--radius-lg)", boxShadow: "var(--elev-1)", overflow: "hidden", marginBottom: 10 };
  const addRowStyle = { width: "100%", display: "flex", alignItems: "center", justifyContent: "center", gap: 7, padding: "10px", border: "none", borderTop: "1px dashed var(--brand-primary-border)", background: "var(--brand-primary-surface)", color: "var(--brand-primary)", fontFamily: "var(--ff-ui)", fontWeight: 700, fontSize: 13, cursor: "pointer" };
  const [doneOnetime, setDoneOnetime] = useState([]);
  const loadDone = useCallback(async () => {
    try { setDoneOnetime((await api("/api/onetime/completed")).items || []); } catch (e) { /* */ }
  }, []);
  useEffect(() => { loadDone(); }, [loadDone]);
  // v20 §11 — po splnení nechaj pripomienku chvíľu v zozname s „Vrátiť", až potom presuň dole
  const [recentDone, setRecentDone] = useState({});
  const recentTimers = useRef({});
  useEffect(() => () => { Object.values(recentTimers.current).forEach((t) => clearTimeout(t)); }, []);
  async function done(idOrItem) {
    // snapshot the item so a finished ONETIME (which leaves s.onetime) can linger too
    const item = (idOrItem && typeof idOrItem === "object") ? idOrItem : null;
    const id = item ? item.id : idOrItem;
    setRecentDone((p) => ({ ...p, [id]: item || true }));
    if (recentTimers.current[id]) clearTimeout(recentTimers.current[id]);
    recentTimers.current[id] = setTimeout(() => {
      setRecentDone((p) => { const n = { ...p }; delete n[id]; return n; });
      delete recentTimers.current[id];
    }, 12000);  // v2 — keep a just-finished item in the list a bit longer before it drops to Splnené
    await api("/api/tasks/" + id + "/done", { method: "POST" }); reload(); loadDone();
  }
  function undoRecent(id) {
    if (recentTimers.current[id]) { clearTimeout(recentTimers.current[id]); delete recentTimers.current[id]; }
    setRecentDone((p) => { const n = { ...p }; delete n[id]; return n; });
    undo(id);
  }
  async function undo(id) { await api("/api/tasks/" + id + "/undo", { method: "POST" }); reload(); loadDone(); }
  async function del(id) { if (!confirm("Odstrániť položku?")) return; await api("/api/tasks/" + id, { method: "DELETE" }); reload(); loadDone(); }
  // v21.10 — potvrdenie zmazania cez modal (nie cez prehliadačový confirm)
  async function confirmDelete() {
    const it = pendingDel; if (!it) return;
    try {
      if (it.shared_event_id) await api("/api/shared/events/" + it.shared_event_id, { method: "DELETE" });
      else await api("/api/tasks/" + it.id, { method: "DELETE" });
    } catch (e) { /* */ }
    setPendingDel(null); setDetail(null); reload(); loadDone();
  }
  async function delShared(eid) { if (!confirm("Odstrániť zdieľanú udalosť? Zmizne obom.")) return; await api("/api/shared/events/" + eid, { method: "DELETE" }); reload(); }
  // recipient removes a reminder a friend shared with them (only from their own list)
  async function dismissShared(id) { if (!confirm("Odstrániť z môjho zoznamu? (Autorovi zostane.)")) return; await api("/api/reminders/shared/" + id, { method: "DELETE" }); reload(); }

  // v16 §4 — plan-item type helpers (Pripomienka = note with a termín + notify; Udalosť = event)
  const isEvent = (it) => it.plan_type === "event";
  const eventPast = (it) => isEvent(it) && (() => { const end = it.end_at || it.due_at || ""; return !!end && end.slice(0, 10) < todayKey(); })();

  // merged OPEN items: reminders (not done) + onetime, sorted by due date (no-date last).
  // Past events drop OUT of the list (they stay only in the calendar — §4).
  // v2 — a finished onetime leaves s.onetime; re-inject the recentDone snapshot so it lingers
  const lingeringOnetime = Object.values(recentDone)
    .filter((v) => v && typeof v === "object" && v._kind === "onetime" && !(s.onetime || []).some((o) => o.id === v.id))
    .map((t) => ({ ...t, _kind: "onetime", done: true }));
  const openItems = [
    ...s.reminders.filter((r) => (!r.done || recentDone[r.id]) && !eventPast(r)).map((r) => ({ ...r, _kind: "reminder" })),
    ...s.onetime.map((t) => ({ ...t, _kind: "onetime" })),
    ...lingeringOnetime,
  ].sort((a, b) => (a.due_at || "9999").localeCompare(b.due_at || "9999"));

  const doneReminders = s.reminders.filter((r) => r.done && !recentDone[r.id]).map((r) => ({ ...r, _kind: "reminder" }));
  const doneItems = [...doneReminders, ...doneOnetime.map((t) => ({ ...t, _kind: "onetime" }))];
  const planCategories = [...new Set([...s.onetime, ...s.reminders].map((t) => t.category).filter(Boolean))];  // P1 + v26 fix — aj pripomienky

  // items a friend shared with me — shown inline (tagged "od …"), each removable from my list
  const sharedFromFriends = (s.sharedWithMe || []).map((x) => ({ ...x, _kind: x.kind, _shared: true }));
  // v22.1 — "Zdieľané" covers BOTH reminder-shares AND co-owned shared events (shared_event_id);
  // purely private items go under "Moje".
  const coOwned = openItems.filter((it) => it.shared_event_id);
  const myItems = openItems.filter((it) => !it.shared_event_id);
  const sharedItems = [...coOwned, ...sharedFromFriends];
  const hasShared = sharedItems.length > 0;
  const showMine = planFilter !== "shared";
  const showShared = planFilter !== "mine";
  // K1 — TERMÍN + KATEGÓRIA filtre. „S termínom" = má due_at (dátum/čas); „Bez termínu" = poznámky
  // bez dátumu. Udalosti majú vždy due_at → spadnú pod „S termínom".
  const hasTermin = (it) => !!it.due_at;
  const itemCat = (it) => it.category || "";  // v26 fix — kategória aj pri pripomienkach/udalostiach
  const listItems = [...(showMine ? myItems : []), ...(showShared ? sharedItems : [])]
    .filter((it) => typeFilter === "all" || (typeFilter === "dated") === hasTermin(it))
    .filter((it) => catFilter === "__all" || itemCat(it) === catFilter)
    .sort((a, b) => (a.due_at || "9999").localeCompare(b.due_at || "9999"));
  const filterActive = planFilter !== "all" || typeFilter !== "all" || catFilter !== "__all";
  // calendar shows every dated plan item — reminders/events AND dated one-time tasks (the latter were
  // missing: a reminder created without a notification is stored as a onetime and never appeared).
  const calItems = [
    ...(showMine ? [
      ...s.reminders.map((r) => ({ ...r, _kind: "reminder" })),
      ...(s.onetime || []).filter((x) => x.due_at).map((t) => ({ ...t, _kind: "onetime" })),
    ] : []),
    ...(showShared ? sharedFromFriends.filter((x) => x.due_at) : []),
  ];

  // P2 — split dated plans from note-style ones; H4 — group the undated ones by category
  const datedItems = listItems.filter((it) => it.due_at);  // already due-sorted
  const undatedItems = listItems.filter((it) => !it.due_at);
  const undatedGroups = [];  // [{cat, items}] in first-appearance order, "Bez kategórie" last
  const _gi = {};
  for (const it of undatedItems) {
    const cat = it.category || "";
    if (!(cat in _gi)) { _gi[cat] = undatedGroups.length; undatedGroups.push({ cat, items: [] }); }
    undatedGroups[_gi[cat]].items.push(it);
  }
  undatedGroups.sort((a, b) => (a.cat === "" ? 1 : 0) - (b.cat === "" ? 1 : 0));

  // v2 — single row inside a grouped card (mockup): icon chip · title · when/od · check.
  // Tap opens the centered detail (with Upraviť). No inline pencil/kôš, no type/category tags.
  // v25 §7.5 — the row itself is the shared PlanItemRow (used by the calendar day list too)
  const itemRow = (item, showBorder) => (
    <PlanItemRow key={(item._shared ? "s" : "") + item._kind + item.id}
      item={item} event={isEvent(item)} showBorder={showBorder}
      recentlyDone={recentDone[item.id]} flash={item.id === flashId}
      onOpen={() => { setDetail(item); setDetailEdit(false); }}
      onDone={() => done(item)} onUndo={() => undoRecent(item.id)} />
  );

  return (
    <>
      {/* view switch is a floating pill docked above the bottom nav (see end of return) */}

      {/* v21.10 — potvrdenie zmazania */}
      {pendingDel && (
        <Modal center title="Naozaj zmazať?" icon="delete" maxWidth={380} onClose={() => setPendingDel(null)}>
          <div style={{ ...st.taskDesc, fontSize: 14 }}>Položka <strong style={{ color: "var(--text)" }}>{pendingDel.label}</strong> sa natrvalo odstráni.</div>
          <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
            <Button variant="ghost" onClick={() => setPendingDel(null)}>Zrušiť</Button>
            <Button variant="destructive" block onClick={confirmDelete}><Icon name="delete" size={16} color="currentColor" />Zmazať</Button>
          </div>
        </Modal>
      )}

      {/* tap a row → read-only details; "Upraviť" swaps in the editable form */}
      {detail && (detailEdit && !detail._shared ? (
        /* v22.4 — full reminder/event form; save propagates via shared-event endpoint. Self-wraps as the sheet. */
        <AddCard kind="reminder" task={detail} categories={planCategories}
          onAdded={() => { reload(); loadDone(); }} onCancel={() => { setDetail(null); setDetailEdit(false); }} />
      ) : (
        <Sheet center
          icon={<Glyph icon={detail.icon} size={20} fallback={detail._kind === "reminder" ? "bell" : "pin"} />}
          iconStyle={iconChipStyle(detail.icon_color)}
          title={detail.label}
          subtitle={[isEvent(detail) ? "Udalosť" : "Pripomienka", detail.overdue ? "po termíne" : null].filter(Boolean).join(" · ")}
          onClose={() => setDetail(null)}
          footer={
            /* v2 — stacked like the návyk detail: Hotovo on top, then Upraviť (ghost) + trash below */
            <div style={{ display: "flex", flexDirection: "column", gap: 9, width: "100%" }}>
              {detail._shared ? (<>
                <Button variant="primary" block onClick={() => { done(detail); setDetail(null); }}><Icon name="check" size={19} color="currentColor" stroke={2.6} />Hotovo</Button>
                <Button variant="ghost" block onClick={() => { dismissShared(detail.id); setDetail(null); }} style={{ color: "var(--danger)" }}><Icon name="delete" size={16} color="currentColor" />Odstrániť z môjho zoznamu</Button>
              </>) : (<>
                {!isEvent(detail) && <Button variant="primary" block onClick={() => { done(detail); setDetail(null); }}><Icon name="check" size={19} color="currentColor" stroke={2.6} />Hotovo</Button>}
                <div style={{ display: "flex", gap: 8 }}>
                  <Button variant="ghost" block onClick={() => setDetailEdit(true)}><Icon name="edit" size={16} color="currentColor" />Upraviť</Button>
                  <IconButton variant="ghost" aria-label="Odstrániť" title="Odstrániť" onClick={() => { setPendingDel(detail); setDetail(null); }} style={{ color: "var(--danger)" }}><Icon name="delete" size={16} color="currentColor" /></IconButton>
                </div>
              </>)}
            </div>
          }>
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <InfoRow icon="calendar" label="Termín"
              value={<span style={detail.overdue ? { color: "var(--danger)", fontWeight: 700 } : undefined}>{detail.due_at ? fmtDuePretty(detail.due_at, detail.end_at) : "bez termínu"}</span>} />
            {detail.repeat && detail.repeat !== "none" && <InfoRow icon="refresh" label="Opakovanie" value={REPEAT_LABEL[detail.repeat] || "Opakuje sa"} />}
            {/* #11 — pri vlastných dátumoch vypíš VŠETKY termíny (minulé prečiarknuté), nie len najbližší */}
            {detail.repeat === "custom" && (detail.custom_dates || []).length > 1 && (
              <InfoRow icon="calendar" label={`Všetky termíny (${detail.custom_dates.length})`}
                value={
                  <span style={{ display: "flex", flexDirection: "column", gap: 3 }}>
                    {[...detail.custom_dates].sort().map((d, i) => {
                      const past = (d || "").slice(0, 10) < todayKey();
                      return <span key={i} style={{ color: past ? "var(--muted)" : "var(--text)", textDecoration: past ? "line-through" : undefined }}>{fmtDuePretty(d)}</span>;
                    })}
                  </span>
                } />
            )}
            {(detail._shared || detail.shared_event_id) && <InfoRow icon="people" label="Zdieľané" value={detail._shared ? ("od " + (detail.owner_name || "priateľa")) : (detail.shared_event_peer ? ("s " + detail.shared_event_peer) : "s priateľom")} />}
            {detail._kind === "reminder" && !detail._shared && detail.notify_offsets && detail.notify_offsets.length > 0 &&
              <InfoRow icon="bell" label="Upozornenie" value={[detail.notify_email ? "e-mail" : null, detail.notify_push ? "v aplikácii" : null].filter(Boolean).join(" + ") + " · " + detail.notify_offsets.map((m) => offLabel(m) + " vopred").join(", ")} />}
            {detail.category && <InfoRow icon="folder" label="Kategória" value={detail.category} />}
            {detail.desc && <InfoRow icon="pen" label="Poznámka" value={detail.desc} />}
          </div>
        </Sheet>
      ))}

      {/* v32 — nadpis „ORGANIZÉR" žije v sticky TopBar (App.jsx title prop), hero h1 odišiel */}
      {/* v32.22 — controls row ako v Dnes tabe: vľavo filter, vpravo tichý ikonový switch
          (Zoznam ⇄ Kalendár). Ceruzka (edit mód) odišla — vytváraš cez „+" v spodnej lište. */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", margin: "6px 2px 12px", position: "relative", gap: 8 }}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 8 }}>
          {/* lievik je od 2026-08 ikona v sade (BuzerIcon „filter") — zdieľaná s filtrom výziev */}
          {view === "list" && openItems.length > 0 && (
            <IconButton variant={filterActive ? "brand" : "surface"} size="sm" icon="filter" onClick={() => setFilterOpen((o) => !o)} aria-label="Filter" title="Filter" />
          )}
        </span>
        <Segmented className="bz-seg--quiet" value={view}
          options={[
            { value: "list", label: <BuzerIcon name="list" size={15} color="currentColor" /> },
            { value: "cal", label: <BuzerIcon name="calendar" size={15} color="currentColor" /> },
          ]}
          onChange={chooseView} />
        {/* filter popover — anchored under the filter button (left) */}
        {filterOpen && view === "list" && openItems.length > 0 && (
          <>
            <div onClick={() => setFilterOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 30 }} />
            <div style={{ position: "absolute", top: "calc(100% + 8px)", left: 0, zIndex: 31, width: "min(86vw, 360px)", background: "var(--surface-solid)", border: "1px solid var(--border)", borderRadius: 18, boxShadow: "var(--shadow-lg)", padding: "16px 16px 18px", maxHeight: "70vh", overflowY: "auto" }}>
                {hasShared && (<>
                  <Caption style={{ display: "block", margin: "0 0 9px" }}>Rozsah</Caption>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 16 }}>
                    {[["all", "Všetko", myItems.length + sharedItems.length], ["mine", "Moje", myItems.length], ["shared", "Zdieľané", sharedItems.length]].map(([k, lbl, n]) => (
                      <FPill key={k} active={planFilter === k} onClick={() => setPlanFilter(k)} label={lbl} count={n} />
                    ))}
                  </div>
                </>)}
                <Caption style={{ display: "block", margin: "0 0 9px" }}>Termín</Caption>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 16 }}>
                  {[["all", "Všetko"], ["dated", "S termínom"], ["undated", "Bez termínu"]].map(([k, lbl]) => (
                    <FPill key={k} active={typeFilter === k} onClick={() => setTypeFilter(k)} label={lbl} />
                  ))}
                </div>
                <Caption style={{ display: "block", margin: "0 0 9px" }}>Kategória</Caption>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  <FPill active={catFilter === "__all"} onClick={() => setCatFilter("__all")} label="Všetky kategórie" />
                  {planCategories.map((c) => <FPill key={c} active={catFilter === c} onClick={() => setCatFilter(c)} label={c} />)}
                  <FPill active={catFilter === ""} onClick={() => setCatFilter("")} label="Bez kategórie" />
                </div>
              </div>
            </>
          )}
      </div>
      {view === "cal" ? (
        <div style={{ marginTop: 4 }}><CalendarTab s={s} reload={reload} items={calItems} undatedItems={undatedItems}
          onAddForDay={(key, type) => { setAddPrefill({ due: key + "T09:00", type: type || "note" }); setAddFromCal(true); setView("list"); setAddOpen(true); }}
          onOpenDetail={(it) => { if (it._shared || it.shared_event_id) chooseView("list"); setDetail({ ...it, _kind: it._kind || "reminder" }); setDetailEdit(false); }} /></div>
      ) : (
        <div style={{ marginTop: 4 }}>
          {datedItems.length > 0 && (
            <>
              <SectionHeader label="S termínom" count={datedItems.length} />
              <div style={groupCard}>{datedItems.map((it, i) => itemRow(it, i > 0))}</div>
            </>
          )}
          {undatedItems.length > 0 && (
            <>
              <SectionHeader label="Bez termínu" count={undatedItems.length} />
              {undatedGroups.map((g) => {
                const showSub = undatedGroups.length > 1 || !!g.cat;
                return (
                  <div key={g.cat || "_none"} style={groupCard}>
                    {showSub && (
                      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "11px 15px 7px" }}>
                        <Caption>{g.cat || "Bez kategórie"}</Caption>
                        <span style={{ flex: 1 }} />
                        <span style={{ fontFamily: "var(--ff-mono)", fontSize: 10, color: "var(--neutral-text-subtle)" }}>{g.items.length}</span>
                      </div>
                    )}
                    {g.items.map((it, i) => itemRow(it, showSub || i > 0))}
                  </div>
                );
              })}
            </>
          )}
          {listItems.length === 0 && <div style={st.empty}>Žiadne úlohy ani pripomienky — pridaj si prvú tlačidlom „+“ dole</div>}

          {/* the create form (opened from the FAB, the edit-mode buttons, or the per-category +Pridať).
              Only mounts when open — no collapsed "+ Pridať" toggle at the bottom. */}
          {addOpen && (
            <div ref={addFormRef} data-plan-addform>
              <AddCard kind="reminder" onAdded={(id) => { reload(); loadDone(); if (id != null) setFlashId(id); }} open={addOpen}
                onOpenChange={(v) => {
                  setAddOpen(v);
                  if (!v) {
                    setAddPrefill(null);
                    // v-e — the form closes on both save and cancel, so this single hook covers both.
                    if (addFromCal) { setView("cal"); setAddFromCal(false); }
                  }
                }} categories={planCategories} prefill={addPrefill} />
            </div>
          )}

          {doneItems.length > 0 && (
            <>
              <SectionHeader label={`Splnené · ${doneItems.length}`} onClick={() => setDoneOpen((o) => !o)} style={{ marginTop: 20 }}
                trailing={<Icon name="chevron" size={14} color="var(--neutral-text-subtle)" style={{ transform: doneOpen ? "rotate(90deg)" : "none", transition: "transform .15s" }} />} />

              {doneOpen && (
                <div style={{ ...groupCard, marginTop: 9 }}>
                  {doneItems.map((item, i) => (
                    <div key={"d" + item._kind + item.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "11px 15px", borderTop: i > 0 ? "1px solid var(--neutral-border)" : "none", opacity: 0.7 }}>
                      <button onClick={() => undo(item.id)} style={{ width: 28, height: 28, borderRadius: 99, border: "none", background: "var(--success)", color: "#fff", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", flexShrink: 0 }} title="Vrátiť späť" aria-label="Vrátiť späť"><Icon name="check" size={15} color="#fff" stroke={3} /></button>
                      <div style={{ flex: 1, minWidth: 0 }}><div style={{ fontWeight: 600, fontSize: 14, color: "var(--neutral-text-subtle)", textDecoration: "line-through" }}>{item.label}</div></div>
                      <button onClick={() => undo(item.id)} style={{ ...st.linkUnderline, flexShrink: 0 }} title="Vrátiť späť">Vrátiť</button>
                      <button onClick={() => del(item.id)} style={st.del} title="Odstrániť"><Icon name="close" size={16} color="currentColor" /></button>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}
    </>
  );
}
